import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated/gql';
import backlinksIcon from '../icons/non-prefilled/16x16/backlinks.svg';
import { LayoutSize } from '../utils/responsive/Sizes';
import useIsLayoutSize from '../utils/responsive/useIsLayoutSize';
import styles from './ControlBar.module.scss';
import ControlBarBacklinks from './ControlBarBacklinks';
import ControlBarInsights from './ControlBarInsights';
import ControlBarPopOver from './ControlBarPopOver';
import {
  isBacklinksMenuOpenAtom,
  isInsightsMenuOpenAtom,
} from './controlBarMenuAtoms';
import PostLastEdited from './topbar/PostLastEdited';
import Timestamp from '../components/Timestamp';

const CONTROL_BAR_POST_QUERY = gql(`
    query ControlBarPostQuery($postId: ID!) {
      post(id: $postId) {
        id
        ...ControlBarBacklinks_post
      }
    }
`);

const INSIGHTS_BUTTON_POST_FRAGMENT = gql(`
  fragment ControlBarInsightsButton_post on Post {
    id
    owner {
      id
      ...AvatarStack_users
    }
    contributors {
      id
      ...AvatarStack_users
    }
    ...ControlBarInsights_post
  }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBar_post on Post {
    id
    ...PostLastEdited_post
    ...ControlBarInsightsButton_post
  }
`);

interface ControlBarInsightsButtonProps {
  post: DocumentType<typeof INSIGHTS_BUTTON_POST_FRAGMENT>;
  children: React.ReactNode;
  popOverPlacement: React.ComponentProps<typeof ControlBarPopOver>['placement'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ControlBarInsightsButton = ({
  post,
  children,
  popOverPlacement,
  open,
  onOpenChange,
}: ControlBarInsightsButtonProps) => {
  const [isShowingConfirmationPopOver, setIsShowingConfirmationPopOver] =
    useState(false);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const users = [
    post.owner,
    ...post.contributors.filter(
      (contributor) => contributor.id !== post.owner.id,
    ),
  ];

  const avatarStack = (
    <div className={styles.avatarStackContainer}>
      <AvatarStack
        size={22}
        limit={4}
        users={users}
        spacing="tight"
        borderSize={2}
        countOverflowVariant="smallPill"
      />
    </div>
  );

  return (
    <div className={styles.insightsButtonContainer}>
      {isMobile ? (
        <div className={styles.mobileInsightsInfo}>
          {avatarStack}
          {children}
        </div>
      ) : (
        <ControlBarPopOver
          onOpenChange={(nextOpen) => {
            if (!isShowingConfirmationPopOver) {
              onOpenChange(nextOpen);
            }
          }}
          hide={isShowingConfirmationPopOver}
          content={
            <ControlBarInsights
              post={post}
              close={() => onOpenChange(false)}
              setIsShowingConfirmationPopOver={setIsShowingConfirmationPopOver}
            />
          }
          placement={popOverPlacement}
          open={open}
        >
          <ControlBarButton active={open}>
            <ControlBarPopOver.Anchor>{avatarStack}</ControlBarPopOver.Anchor>
            {children}
          </ControlBarButton>
        </ControlBarPopOver>
      )}
    </div>
  );
};

const FOOTER_CONTROL_BAR_POST_FRAGMENT = gql(`
  fragment FooterControlBar_post on Post {
    id
    insertedAt
    ...ControlBarInsightsButton_post
  }
`);

interface FooterControlBarProps {
  post: DocumentType<typeof FOOTER_CONTROL_BAR_POST_FRAGMENT>;
}

export const FooterControlBar = ({ post }: FooterControlBarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ControlBarInsightsButton
      open={isOpen}
      onOpenChange={setIsOpen}
      popOverPlacement="top-end"
      post={post}
    >
      {post.archivedAt && (
        <>
          Archived <Timestamp date={post.archivedAt} />
        </>
      )}
      {!post.archivedAt && post.publishedAt && (
        <>
          Published <Timestamp date={post.publishedAt} />
        </>
      )}
      {!post.archivedAt && !post.publishedAt && (
        <>
          Created <Timestamp date={post.insertedAt} />
        </>
      )}
    </ControlBarInsightsButton>
  );
};

interface ControlBarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  children: React.ReactNode;
}

const ControlBarButton = React.forwardRef<
  HTMLButtonElement,
  ControlBarButtonProps
>(({ active, children, ...rest }, ref) => (
  <Button
    {...rest}
    ref={ref}
    className={classNames(styles.button, {
      [styles.active]: active,
      [styles.disabled]: rest.disabled,
    })}
  >
    {children}
  </Button>
));

const ControlBarIconButton = React.forwardRef<
  HTMLButtonElement,
  ControlBarButtonProps
>(({ active, children, ...rest }, ref) => (
  <Button
    ref={ref}
    {...rest}
    className={classNames(styles.iconButton, {
      [styles.active]: active,
      [styles.disabled]: rest.disabled,
    })}
  >
    {children}
  </Button>
));

interface ControlBarProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ControlBar = ({ post }: ControlBarProps) => {
  const [isBacklinksMenuOpen, setIsBacklinksMenuOpen] = useAtom(
    isBacklinksMenuOpenAtom,
  );
  const [isInsightsMenuOpen, setIsInsightsMenuOpen] = useAtom(
    isInsightsMenuOpenAtom,
  );

  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const { data } = useQuery(CONTROL_BAR_POST_QUERY, {
    variables: {
      postId: post.id,
    },
  });

  const postWithBacklinks = data?.post;

  useEffect(() => {
    if (isMobile && isInsightsMenuOpen) {
      setIsInsightsMenuOpen(false);
    }
  }, [isMobile, setIsInsightsMenuOpen, isInsightsMenuOpen]);

  useEffect(() => {
    if (isMobile && isBacklinksMenuOpen) {
      setIsBacklinksMenuOpen(false);
    }
  }, [isMobile, setIsBacklinksMenuOpen, isBacklinksMenuOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <ControlBarInsightsButton
          open={isInsightsMenuOpen}
          onOpenChange={setIsInsightsMenuOpen}
          popOverPlacement="bottom-end"
          post={post}
        >
          <PostLastEdited post={post} showInitial />
        </ControlBarInsightsButton>
      </div>

      <div className={styles.right}>
        {!isMobile &&
          postWithBacklinks &&
          postWithBacklinks.backlinks.filter(
            (backlink) => backlink.id !== postWithBacklinks.id,
          ).length > 0 && (
            <ControlBarPopOver
              content={<ControlBarBacklinks post={postWithBacklinks} />}
              open={isBacklinksMenuOpen}
              placement="bottom-start"
              onOpenChange={setIsBacklinksMenuOpen}
            >
              <ControlBarIconButton active={isBacklinksMenuOpen}>
                <SVGIcon>{backlinksIcon}</SVGIcon>
              </ControlBarIconButton>
            </ControlBarPopOver>
          )}
      </div>
    </div>
  );
};

export default ControlBar;
